import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { ModalComponent } from '@app/shared/dialogs/modal/modal.component';
import { ButtonThemeColor } from '@progress/kendo-angular-buttons';

export interface QuestionComponentButton {
  text: string;
  value?: any;
  primary?: boolean;
  outline?: boolean;
  danger?: boolean;
  green?: boolean;
  warning?: boolean;
  closeAction?: boolean;
  hidden?: boolean;
  handler?: (value?: string) => Promise<{ close: boolean }> | any;
}

@Component({
  standalone: false,
  selector: 'ti-question',
  templateUrl: 'question.component.html',
  styleUrls: ['question.component.scss'],
})
export class QuestionComponent implements OnInit, AfterViewInit {
  strings = tiStrings.app.misc;
  readonly localStorageKeys = LocalStorageKeys;

  @Input() id: string;
  @Input() title: string;
  @Input() text: string | SafeHtml;
  @Input() buttons: QuestionComponentButton[];
  @Input() icon: string;
  @Input() isLoading: boolean = false;
  @Input() width: number;
  @Input() height: number;
  @Input() minWidth: number;
  @Input() autoCloseDelayTime: number; //seconds
  @Input() denyClose: boolean = false;
  @Input() dangerTitle: boolean = false;
  /** Opt-out of the automatic red title that a `danger` button triggers, keeping the title neutral. */
  @Input() suppressDangerTitle: boolean = false;
  @Output() close = new EventEmitter<{ value: string; noMoreQuestions: boolean }>();
  maxWidth: number = 0;
  html: SafeHtml;
  warningText: string;
  isWarning: boolean;
  isSuccess: boolean;
  counter: number; // seconds

  noMoreQuestions = false;
  closeButton: QuestionComponentButton;
  @ViewChild('modal') public modal: ModalComponent;

  constructor(private readonly sanitizer: DomSanitizer) {}

  ngOnInit() {
    if (this.autoCloseDelayTime) {
      if (this.modal) {
        this.modal.height = 100;
      }
      this.startTimer(this.autoCloseDelayTime);
    }
    this.maxWidth = Math.round(window.innerWidth / 2);
    this.closeButton = this.buttons.find((button) => button.closeAction) || {
      text: tiStrings.app.misc.close,
      value: 'close',
    };

    this.title = this.sanitizer.bypassSecurityTrustHtml(this.title) as string;
    if (!this.autoCloseDelayTime) {
      this.html = this.sanitizer.bypassSecurityTrustHtml(this.text as string);
    }

    this.isWarning = this.buttons.some((btn) => {
      return btn.danger;
    });

    this.isSuccess = this.buttons.some((btn) => {
      return btn.green;
    });
  }

  getViewerDetectedMessage(): string {
    return (
      this.strings.beforeSignOutSaveChanges +
      '<br/><br/><b>' +
      this.strings.doNotCloseBrowser +
      '</b>' +
      '<br/><br/>' +
      tiStringsFormat(this.strings.timeOutMessagePattern, this.text, this.counter)
    );
  }

  private startTimer(initialValue: number): void {
    this.counter = initialValue;
    this.html = this.sanitizer.bypassSecurityTrustHtml(this.getViewerDetectedMessage());
    let timerId = setInterval(() => {
      if (this.counter > 0) {
        this.counter--;
        if (this.counter !== 0) {
          this.html = this.sanitizer.bypassSecurityTrustHtml(this.getViewerDetectedMessage());
        } else {
          clearInterval(timerId);
          this.close.emit({ value: null, noMoreQuestions: true });
        }
      }
    }, 1000);
  }

  getButtonThemeColor(button: QuestionComponentButton): ButtonThemeColor {
    if (button?.danger) {
      return 'error';
    }
    if (button?.warning) {
      return 'warning';
    }
    if (button?.green) {
      return 'success';
    }
    if (button?.primary) {
      return 'primary';
    }
    return 'tertiary';
  }

  buttonClick(button: QuestionComponentButton, byClose?: boolean) {
    if (button.handler) {
      this.doAction(button.value, () => button.handler(button.value));
    } else {
      this.close.emit({ value: button.value, noMoreQuestions: this.noMoreQuestions && !byClose });
    }
  }

  private doAction(result: 'yes' | 'no', handler: (value: string) => Promise<{ close: boolean }> | any) {
    this.isLoading = true;
    const value = handler && handler(result);
    if (value instanceof Promise /*<{ close: boolean }>*/) {
      value
        .then((handlerRes) => {
          this.isLoading = false;

          if (handlerRes.close) {
            this.close.emit({ value: result, noMoreQuestions: true });
          }
        })
        .catch(() => (this.isLoading = false));
    } else {
      this.isLoading = false;
      if (value?.close || !value) {
        this.close.emit({ value: result, noMoreQuestions: true });
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.autoCloseDelayTime && this.modal) {
      //this.modal.maxHeight = 100;
      this.modal.minHeight = 140;
    }
  }
}
